import React from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Microscope, Globe, Users, ArrowRight, Building2, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  return (
    <div className="min-h-screen bg-[#F5F2E8]">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden bg-[#1A1A1A]">
        <div className="absolute inset-0 data-grid opacity-30"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white pt-12">
          <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-4 font-['IBM_Plex_Sans_Condensed'] uppercase">CORPORATE DOCUMENTATION</p>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-[0.05em] mb-6 font-['IBM_Plex_Sans_Condensed'] uppercase">
            ABOUT TASTYJELLY<br />GLOBAL INDUSTRIES
          </h1>
          <p className="text-base md:text-lg text-[#6B6B6B] max-w-2xl mx-auto leading-relaxed font-['IBM_Plex_Sans']">
            A Fortune 5,000,000 company dedicated to disrupting the backcountry gear industry, 
            one meticulously-engineered product at a time.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase">INSTITUTIONAL HISTORY</p>
              <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
                FROM GARAGE TO<br />GLOBAL POWERHOUSE*
              </h2>
              <div className="space-y-4 text-[#5A5A5A] leading-relaxed font-['IBM_Plex_Sans']">
                <p>
                  TastyJelly Global Industries was founded with a singular vision: to create the world's 
                  most over-engineered backcountry gear. What started as one person's obsession with 
                  spray cheese accessibility has evolved into a three-product empire.
                </p>
                <p>
                  Our R&D facility (a converted garage) houses our entire product development team 
                  (one person) who works tirelessly to bring you solutions to problems you didn't 
                  know you had.
                </p>
                <p>
                  Today, TastyJelly Global Industries serves customers across the globe with our 
                  revolutionary Mega Fiskur Pro nutrition system, the patented Spray Cheese Holster, 
                  and the industry-disrupting TJNOC wine skin technology.
                </p>
                <p className="text-xs text-[#6B6B6B] border-l-2 border-[#4A7C29] pl-4">
                  *Still operating from the garage. "Global Powerhouse" is aspirational.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden bg-[#F5F2E8] border-2 border-[#E0DDD4]">
                <img 
                  src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800"
                  alt="R&D Facility"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#1A1A1A] text-white p-4">
                <p className="text-[10px] text-[#6B6B6B] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider">EST.</p>
                <p className="text-2xl font-semibold font-['IBM_Plex_Mono']">2024</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-[#F5F2E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase">PERFORMANCE METRICS</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
              KEY PERFORMANCE INDICATORS
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '3', label: 'Products in Portfolio', icon: Building2 },
              { value: '1', label: 'Full-Time Employee', icon: Users },
              { value: '847', label: 'Calories per Mega Fiskur', icon: TrendingUp },
              { value: '0', label: 'Venture Capital Raised', icon: Globe },
            ].map((stat, index) => (
              <div key={index} className="bg-white p-6 border-2 border-[#E0DDD4] text-center">
                <stat.icon className="w-5 h-5 text-[#4A7C29] mx-auto mb-3" />
                <p className="text-2xl font-semibold text-[#1A1A1A] font-['IBM_Plex_Mono']">{stat.value}</p>
                <p className="text-[#5A5A5A] text-xs mt-1 font-['IBM_Plex_Sans']">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase">CORPORATE VALUES</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
              GUIDING PRINCIPLES
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: FlaskConical, 
                title: 'INNOVATION', 
                desc: 'We believe in solving problems that don\'t exist yet. Our R&D department (one person in a garage) works around the clock to engineer solutions of questionable necessity.' 
              },
              { 
                icon: Microscope, 
                title: 'PRECISION', 
                desc: 'Every product undergoes rigorous testing protocols. We\'ve used each product at least once. Sometimes twice. The data speaks for itself.' 
              },
              { 
                icon: Globe, 
                title: 'GLOBAL IMPACT', 
                desc: 'We ship worldwide because the internet exists. Our environmental impact statement is pending. Our carbon footprint is "probably fine."' 
              },
            ].map((item, index) => (
              <div key={index} className="bg-[#F5F2E8] p-8 border-2 border-[#E0DDD4]">
                <div className="w-12 h-12 bg-white flex items-center justify-center mb-6 border border-[#E0DDD4]">
                  <item.icon className="w-6 h-6 text-[#4A7C29]" />
                </div>
                <h3 className="font-semibold text-base text-[#1A1A1A] mb-3 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">{item.title}</h3>
                <p className="text-[#5A5A5A] leading-relaxed font-['IBM_Plex_Sans'] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-[#1A1A1A] text-white relative overflow-hidden">
        <div className="absolute inset-0 data-grid opacity-30"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="w-14 h-14 bg-[#4A7C29] flex items-center justify-center mx-auto mb-8">
            <FlaskConical className="w-7 h-7 text-white" />
          </div>
          <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-4 font-['IBM_Plex_Sans_Condensed'] uppercase">MISSION STATEMENT</p>
          <h2 className="text-xl md:text-2xl font-semibold mb-8 leading-relaxed font-['IBM_Plex_Sans']">
            "To leverage synergistic paradigms in backcountry gear technology, 
            delivering best-in-class solutions that optimize the outdoor experience 
            through innovation, disruption, and an unhealthy obsession with spray cheese."
          </h2>
          <p className="text-[#6B6B6B] mb-10 font-['IBM_Plex_Sans'] text-sm">— TastyJelly Global Industries, 2024 Annual Report (unpublished)</p>
          <Link to="/products">
            <Button 
              className="rounded-none bg-[#4A7C29] text-white font-semibold px-8 py-6 text-sm hover:bg-[#3D6822] transition-colors font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider"
              data-testid="about-shop-btn"
            >
              VIEW PRODUCT CATALOG
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-12 bg-[#F5F2E8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-[#6B6B6B] text-center leading-relaxed font-['IBM_Plex_Sans']">
            TastyJelly Global Industries is not actually traded on any stock exchange. "Global Industries" 
            is a registered trademark of TastyJelly Global Industries LLC (pending). All claims of 
            multinational corporate status are satirical. We are a small cottage gear company. 
            Please buy our products. The Mega Fiskur Pro contains fish. The TJNOC is for wine, not water.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
